import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { LogOnMount } from '@mightyhive/analytics';
import {
  AppGradientSurface,
  LibraryDetailHeader,
  LibrarySectionSurface,
  LibraryTabBar,
  libraryTabId,
  libraryTabPanelId,
} from '@mightyhive/commoncomponents';
import { APP_LAYOUT_SIZES } from '@mightyhive/theme';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RouteLocations } from './RouteLocations';
import { LibraryAnalysisTab } from './library/LibraryAnalysisTab';
import { LibraryDesignTab } from './library/LibraryDesignTab';
import { LibraryPlanTab } from './library/LibraryPlanTab';
import { EditExperimentDetailsDialog } from './library/components/EditExperimentDetailsDialog';
import { formatLibraryCardStatus } from './library/libraryStatus';
import { apiFetch } from '../utils/api';
import {
  ExperimentDetailsUpdateError,
  updateExperimentDetails,
  type ExperimentDetailsValues,
} from '../utils/experimentDetails';
import { formatExperimentCreatedDate, getMethodologyLabel } from '../utils/geoliftLabels';
import type { LibraryDetailResponse, LibraryTab, LibraryTabId } from '../utils/libraryTypes';

const LIBRARY_TAB_IDS: LibraryTabId[] = ['design', 'plan', 'analysis'];

function isLibraryTabId(value: string): value is LibraryTabId {
  return LIBRARY_TAB_IDS.includes(value as LibraryTabId);
}

function resolveActiveTab(
  tabs: LibraryTab[],
  defaultTab: string | null,
  queryTab: string | null,
): string | null {
  if (tabs.length === 0) return null;
  const tabIds = new Set(tabs.map((tab) => tab.id));
  if (queryTab && tabIds.has(queryTab)) return queryTab;
  if (defaultTab && tabIds.has(defaultTab)) return defaultTab;
  return tabs[0]?.id ?? null;
}

export function LibraryDetail(): JSX.Element {
  const navigate = useNavigate();
  const { externalUuid } = useParams<{ externalUuid: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<NonNullable<LibraryDetailResponse['data']> | null>(null);
  const [activatedTabs, setActivatedTabs] = useState<Set<string>>(new Set());
  const [editingDetails, setEditingDetails] = useState(false);

  useEffect(() => {
    if (!externalUuid) {
      setLoading(false);
      setError('Experiment not found.');
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    apiFetch(`/api/experiments/${externalUuid}/library/`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: LibraryDetailResponse) => {
        if (!active) return;
        if (!data.data) {
          setError('Failed to load experiment detail.');
          return;
        }
        setDetail(data.data);
      })
      .catch((status: unknown) => {
        if (!active) return;
        if (status === 401 || status === 403) {
          window.location.href = `/login?next=${encodeURIComponent(window.location.href)}`;
          return;
        }
        if (status === 404) {
          setError('Experiment not found.');
          return;
        }
        setError('Failed to load experiment detail.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [externalUuid]);

  const queryTab = searchParams.get('tab');
  const activeTab = useMemo(() => {
    if (!detail) return null;
    return resolveActiveTab(detail.tabs, detail.default_tab, queryTab);
  }, [detail, queryTab]);

  useEffect(() => {
    if (!detail || !activeTab) return;
    if (queryTab === activeTab) return;
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, detail, queryTab, setSearchParams]);

  useEffect(() => {
    if (!activeTab) return;
    setActivatedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  function handleTabChange(tabId: string): void {
    if (!isLibraryTabId(tabId)) return;
    setSearchParams({ tab: tabId });
  }

  function handleEdit(url: string | null): void {
    if (!url) return;
    navigate(url);
  }

  async function handleSaveDetails(values: ExperimentDetailsValues): Promise<void> {
    if (!externalUuid) return;
    try {
      const updated = await updateExperimentDetails(externalUuid, values);
      setDetail((current) => {
        if (!current) return current;
        return {
          ...current,
          experiment: {
            ...current.experiment,
            name: updated.name,
            description: updated.description,
          },
          header: {
            ...current.header,
            description: updated.description,
          },
        };
      });
    } catch (saveError) {
      if (
        saveError instanceof ExperimentDetailsUpdateError &&
        (saveError.status === 401 || saveError.status === 403)
      ) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.href)}`;
        return;
      }
      throw saveError;
    }
  }

  if (loading) {
    return (
      <AppGradientSurface maxWidth={APP_LAYOUT_SIZES.libraryContentMaxWidth}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </AppGradientSurface>
    );
  }

  if (error || !detail || !externalUuid) {
    return (
      <AppGradientSurface maxWidth={APP_LAYOUT_SIZES.libraryContentMaxWidth}>
        <Alert severity="error">{error ?? 'Experiment not found.'}</Alert>
      </AppGradientSurface>
    );
  }

  const experimentUuid = externalUuid;
  const { experiment, header, tabs, actions, design } = detail;

  function renderTabPanel(tabId: string): JSX.Element | null {
    if (!activatedTabs.has(tabId)) return null;

    if (tabId === 'design') {
      return (
        <LibraryDesignTab
          externalUuid={experimentUuid}
          design={design}
          editDesignUrl={actions.edit_design.url}
          onEditDesign={
            actions.edit_design.visible && actions.edit_design.url
              ? () => handleEdit(actions.edit_design.url)
              : undefined
          }
        />
      );
    }
    if (tabId === 'plan') {
      return (
        <LibraryPlanTab
          externalUuid={experimentUuid}
          methodologyType={header.methodology_type}
        />
      );
    }
    if (tabId === 'analysis') {
      return (
        <LibraryAnalysisTab
          externalUuid={experimentUuid}
          editAnalysisUrl={actions.edit_analysis.url}
          editAnalysisLabel={actions.edit_analysis.label}
          onEditAnalysis={() => handleEdit(actions.edit_analysis.url)}
        />
      );
    }
    return null;
  }

  return (
    <AppGradientSurface maxWidth={APP_LAYOUT_SIZES.libraryContentMaxWidth}>
      <LogOnMount eventName="Page View: Library Detail" />

      <LibraryDetailHeader
        breadcrumbs={[
          { label: 'Library', to: RouteLocations.LIBRARY },
          { label: experiment.name },
        ]}
        title={experiment.name}
        statusLabel={formatLibraryCardStatus(header.status_label)}
        typeLabel={experiment.type_label}
        owner={header.owner}
        createdAtLabel={formatExperimentCreatedDate(experiment.created_at)}
        description={header.description}
        methodologyLabel={getMethodologyLabel(header.methodology_type)}
        actions={actions}
        onEditDetails={() => setEditingDetails(true)}
        onEditDesign={
          actions.edit_design.visible && actions.edit_design.url
            ? () => handleEdit(actions.edit_design.url)
            : undefined
        }
        onEditAnalysis={
          actions.edit_analysis.visible && actions.edit_analysis.url
            ? () => handleEdit(actions.edit_analysis.url)
            : undefined
        }
      />

      <EditExperimentDetailsDialog
        open={editingDetails}
        initialValues={{
          name: experiment.name,
          description: header.description,
        }}
        onClose={() => setEditingDetails(false)}
        onSave={handleSaveDetails}
      />

      {tabs.length > 0 && activeTab ? (
        <>
          <LibraryTabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
          {tabs.map((tab) => (
            <Box
              key={tab.id}
              role="tabpanel"
              id={libraryTabPanelId(tab.id)}
              aria-labelledby={libraryTabId(tab.id)}
              hidden={tab.id !== activeTab}
              sx={{ display: tab.id === activeTab ? 'block' : 'none' }}
            >
              {renderTabPanel(tab.id)}
            </Box>
          ))}
        </>
      ) : (
        <LibrarySectionSurface>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            No persisted design, plan, or analysis data yet.
          </Typography>
        </LibrarySectionSurface>
      )}
    </AppGradientSurface>
  );
}
